<div class="pr-wrapper">
    @if (!isSuccess()) {
        <form
            class="pr-card d-flex flex-column rounded bg-light border"
            [formGroup]="formGroup"
        >
            <input type="hidden" formControlName="categoryName" />
            <input type="hidden" formControlName="priorityName" />

            <!-- #region Header -->
            <div
                class="d-flex flex-wrap align-items-center justify-content-between border-bottom p-3 pr-header rounded-top"
            >
                <h2 class="mb-0">Register for an account</h2>
                <img
                    class="pr-logo"
                    src="assets/images/logos/logo_small.png"
                    alt="HOMER"
                />
            </div>
            <!-- #endregion -->

            <div class="p-3 border-bottom">
                Fill out your information and select your requested facility below. This submits an account
                request to the BPHP support staff, who will follow up with next steps.
            </div>

            <!-- #region Contact info -->
            <div class="d-flex flex-column border-bottom">
                <div class="d-flex flex-column flex-md-row p-3 pb-2">
                    <div class="w-100 mr-md-2 mb-3 mb-md-0">
                        @let fullNameError = errorFor('fullName');
                        <p-floatlabel variant="on">
                            <p-iconfield>
                                <input
                                    pInputText
                                    id="pr-full-name"
                                    class="w-100"
                                    formControlName="fullName"
                                    maxlength="250"
                                    autocomplete="name"
                                    [attr.aria-invalid]="fullNameError ? 'true' : null"
                                    [attr.aria-describedby]="fullNameError ? 'pr-full-name-error' : null"
                                />
                                <p-inputicon styleClass="pi pi-user" />
                            </p-iconfield>
                            <label for="pr-full-name">Name</label>
                        </p-floatlabel>
                        @if (fullNameError) {
                            <small
                                id="pr-full-name-error"
                                class="text-danger d-block mt-1"
                                >{{ fullNameError }}</small
                            >
                        }
                    </div>
                    <div class="w-100">
                        @let emailError = errorFor('email');
                        <p-floatlabel variant="on">
                            <p-iconfield>
                                <input
                                    pInputText
                                    id="pr-email"
                                    class="w-100"
                                    formControlName="email"
                                    maxlength="100"
                                    autocomplete="email"
                                    (blur)="sanitizeEmail()"
                                    [attr.aria-invalid]="emailError ? 'true' : null"
                                    [attr.aria-describedby]="emailError ? 'pr-email-error' : null"
                                />
                                <p-inputicon styleClass="pi pi-envelope" />
                            </p-iconfield>
                            <label for="pr-email">Email</label>
                        </p-floatlabel>
                        @if (emailError) {
                            <small
                                id="pr-email-error"
                                class="text-danger d-block mt-1"
                                >{{ emailError }}</small
                            >
                        }
                    </div>
                </div>

                <div class="d-flex flex-column flex-md-row p-3 pt-2">
                    <div class="w-100 mr-md-2 mb-3 mb-md-0">
                        @let facilityError = errorFor('facilityId');
                        <p-floatlabel variant="on">
                            <p-select
                                inputId="pr-facility"
                                styleClass="w-100"
                                dropdownIcon="pi pi-building"
                                [filter]="true"
                                [options]="facilities()"
                                optionLabel="text"
                                optionValue="value"
                                formControlName="facilityId"
                                (onChange)="facilityChange()"
                            />
                            <label for="pr-facility">Facility</label>
                        </p-floatlabel>
                        @if (facilityError) {
                            <small
                                id="pr-facility-error"
                                class="text-danger d-block mt-1"
                                >{{ facilityError }}</small
                            >
                        }
                    </div>
                    <div class="w-100">
                        @let phoneError = errorFor('phoneNumber');
                        <p-floatlabel variant="on">
                            <p-iconfield>
                                <p-inputmask
                                    inputId="pr-phone"
                                    styleClass="w-100"
                                    mask="(999) 999-9999"
                                    autocomplete="tel"
                                    formControlName="phoneNumber"
                                />
                                <p-inputicon styleClass="pi pi-phone" />
                            </p-iconfield>
                            <label for="pr-phone">Phone Number</label>
                        </p-floatlabel>
                        @if (phoneError) {
                            <small
                                id="pr-phone-error"
                                class="text-danger d-block mt-1"
                                >{{ phoneError }}</small
                            >
                        }
                    </div>
                </div>
            </div>
            <!-- #endregion -->

            <!-- #region Request -->
            <div class="d-flex flex-column p-3">
                <div class="w-100 mb-3">
                    @let subjectError = errorFor('subject');
                    <p-floatlabel variant="on">
                        <p-iconfield>
                            <input
                                pInputText
                                id="pr-subject"
                                class="w-100"
                                formControlName="subject"
                                maxlength="200"
                                [attr.aria-invalid]="subjectError ? 'true' : null"
                                [attr.aria-describedby]="subjectError ? 'pr-subject-error' : null"
                            />
                            <p-inputicon styleClass="pi pi-book" />
                        </p-iconfield>
                        <label for="pr-subject">Brief Subject</label>
                    </p-floatlabel>
                    @if (subjectError) {
                        <small
                            id="pr-subject-error"
                            class="text-danger d-block mt-1"
                            >{{ subjectError }}</small
                        >
                    }
                </div>
                <div class="w-100">
                    @let descriptionError = errorFor('description');
                    <p-floatlabel variant="on">
                        <textarea
                            pTextarea
                            id="pr-description"
                            class="w-100"
                            formControlName="description"
                            rows="5"
                            [maxlength]="descriptionMaxLength"
                            style="resize: vertical"
                            [attr.aria-invalid]="descriptionError ? 'true' : null"
                            [attr.aria-describedby]="
                                descriptionError ? 'pr-description-error' : 'pr-description-count'
                            "
                        ></textarea>
                        <label for="pr-description">Description</label>
                    </p-floatlabel>
                    <div class="d-flex align-items-start mt-1">
                        @if (descriptionError) {
                            <small
                                id="pr-description-error"
                                class="text-danger"
                                >{{ descriptionError }}</small
                            >
                        }
                        <small
                            id="pr-description-count"
                            class="text-muted ml-auto pl-2 text-nowrap"
                        >
                            {{ descriptionLength() | number }} / {{ descriptionMaxLength | number }}
                        </small>
                    </div>
                </div>
            </div>
            <!-- #endregion -->

            <!-- #region Buttons -->
            <div class="d-flex justify-content-center border-top p-3">
                <p-button
                    label="Cancel"
                    severity="secondary"
                    class="mr-2"
                    (onClick)="router.navigate(['/login'])"
                />
                <p-button
                    label="Submit"
                    raised
                    severity="info"
                    [loading]="isSubmitting()"
                    (onClick)="onSubmit()"
                />
            </div>
            <!-- #endregion -->

            <!-- Honeypot: hidden from real users; bots that fill it are rejected. -->
            <input
                class="pr-honeypot"
                type="checkbox"
                formControlName="honeyPot"
                tabindex="-1"
                autocomplete="off"
                aria-hidden="true"
            />
        </form>
    } @else {
        <div class="pr-card rounded bg-light border p-4 text-center">
            <i
                class="pi pi-check-circle text-success"
                style="font-size: 2.5rem"
            ></i>
            <h3 class="mt-2">Request submitted</h3>
            <p class="mb-3">
                Your account request has been submitted. BPHP support staff have been notified and will follow
                up with next steps.
            </p>
            <p-button
                label="Return"
                raised
                severity="info"
                (onClick)="router.navigate(['/login'])"
            />
        </div>
    }
</div>
